<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { onMount } from 'svelte';
  import Close from '../../icons/Close.svelte';
  import {
    AppState,
    SetIconsBackground,
    SetNotification
  } from '../../../etc/stores';
  import MessagePopup from '../../misc/MessagePopup.svelte';
  import ThemedIcon from '../../misc/ThemedIcon.svelte';
  import Spinner from '../../misc/Spinner.svelte';
  import Link from '../../misc/Link.svelte';
  import { cdn, copyTextToClipboard } from '../../../etc/utils';
  import Analytics from '../../../services/analytics';
  import PoweredBy from '../../misc/PoweredBy.svelte';
  import { replaceVariables } from '../../../i18n';
  import RewardCoin from '../../misc/RewardCoin.svelte';
  import Heading from '../../typography/Heading.svelte';
  import Text from '../../typography/Text.svelte';
  import flagKeys from '../../../etc/flagKeys';
  import Alert from '../../alert/Alert.svelte';
  import ExpiringCountdown from '../../expiring-countdown/ExpiringCountdown.svelte';

  const couponVariant =
    $AppState.website.notificationParams?.couponVariant || 'free-coins';
  const couponData =
    $AppState.website.notificationParams?.couponData ||
    $AppState.freeCoinsCampaign.coupon;

  let loading = false;
  let buttonClass = '';

  $: expirableCoinsIsActive = $AppState.featureFlags.includes(flagKeys.EXPIRABLE_COINS);

  const t = (text: string, values: any = {}) => {
    return replaceVariables($_(`welcome_coin_saved_popup.${text}`), values);
  };

  const closeMessage = () => {
    SetNotification('');
  };

  const goToWallet = () => {
    AppState.update(state => ({
      ...state,
      website: {
        ...state.website,
        popoverOpen: true,
        popoverRouteOnOpen: {
          route: '/dashboard'
        }
      }
    }));
    Analytics.logEvent('click-go-wallet', 'free-coins-coupon-saved-state');

    closeMessage();
  };

  const closeAfterDelay = () => {
    setTimeout(() => {
      SetNotification('');
    }, 2000);
  };

  const copyCode = () => {
    buttonClass = '-copied';
    Analytics.logEvent('click-copy-coupon', 'free-coins-coupon-saved-state');
    copyTextToClipboard(couponData.code);

    closeAfterDelay();
  };

  const onClickClose = () => {
    Analytics.logEvent('click-close', 'free-coins-coupon-saved-state');
    closeMessage();
  };

  onMount(() => {
    SetIconsBackground('light-without-background');
  });
</script>

<MessagePopup>
  <div class="welcome-coin-saved--wrapper">
    <div class="welcome-coin-saved--close" on:click={onClickClose}>
      <ThemedIcon>
        <Close />
      </ThemedIcon>
    </div>
    <div class="welcome-coin-saved--top">
      <div class="welcome-coin-saved--top--background" />
      <div
        class="welcome-coin-saved--top--confetti"
        style="background-image: url({cdn('/images/confetti.svg')})"
      />
      <RewardCoin
        size="152"
        value={couponData.moneyValue / 100}
        type={couponVariant === 'referral' ? 'percentage' : 'default'}
      />
    </div>
    <div class="welcome-coin-saved--content">
      <Heading type="h2" textAlign="center">{t('title')}</Heading>
      <Text type="default" textAlign="center">{t('description')}</Text>
    </div>
    <div class="welcome-coin-saved--bottom">
      <div class="welcome-coin-saved__actions-btn">
        {#if expirableCoinsIsActive && couponData?.expiresAt}
        <Alert align="center">
          <ExpiringCountdown
            showExpiredLabel
            emitExpiredEventOnFirstTime
            expiresAt={couponData.expiresAt}
          />
        </Alert>
        {/if}
        <div
          data-code={couponData.code}
          class="welcome-coin-saved--button {buttonClass}"
          role="button"
          on:click={copyCode}
        >
          {#if loading}
            <Spinner color={$AppState.theme.colors.textAboveBackground} />
          {:else}
            <div class="welcome-coin-saved--button--copied">
              <p>{t('btn_copied')}</p>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="8.00004" cy="7.99992" r="6.66667" fill="white" />
                <path
                  d="M10.6667 6L7.00004 10L5.33337 8.18182"
                  stroke="#23DA6E"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <p>{t('btn')}</p>
          {/if}
        </div>
      </div>
      <div class="welcome-coin-saved--bottom--link">
        <Link fontSize="14px" lineHeight="20px" on:click={goToWallet}>
          {t('link')}
        </Link>
      </div>
    </div>
    <PoweredBy />
  </div>
</MessagePopup>

<style lang="scss">
  @use './styles.scss' as *;

  .welcome-coin-saved {
    &__actions-btn {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 16px;

      :global(.twm-expiring-countdown) {
        font-weight: 500;
      }
    }

    &--wrapper {
      display: flex;
      flex-flow: column nowrap;
      align-items: center;
      width: 100%;
    }

    &--close {
      position: absolute;
      top: 16px;
      right: 16px;
      cursor: pointer;
      z-index: 2;
    }

    &--top {
      width: 100%;
      display: flex;
      flex-flow: column nowrap;
      align-items: center;
      padding-top: 32px;
      padding-bottom: 24px;
      z-index: 1;

      &--background {
        position: absolute;
        top: 0;
        height: 145px;
        width: 100%;
        background-color: var(--twism-theme-freeCoinsCampaign-background);
        border-top-left-radius: 16px;
        border-top-right-radius: 16px;
      }

      &--confetti {
        position: absolute;
        top: 0;
        height: 142px;
        width: 100%;
        background-repeat: no-repeat;
        background-position: top;
        background-size: 100% 122px;
      }
    }

    &--content {
      padding: 0 24px;
      :global(h2) {
        margin-bottom: 8px !important;
      }
    }

    &--bottom {
      display: flex;
      flex-flow: column nowrap;
      align-items: center;
      width: 100%;
      box-sizing: border-box;
      padding: 38px 16px 24px;

      &--link {
        margin-top: 24px;
      }

      @media screen and (max-height: 620px) {
        padding-top: 24px;
        padding-bottom: 16px;
      }
    }

    &--button {
      width: 100%;
      border-radius: 8px;
      overflow: hidden;
      text-decoration: none;
      box-sizing: border-box;
      outline: none;
      height: 48px;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.3s ease-in-out;
      color: var(--twism-theme-textAboveBackground);
      background: var(--twism-theme-backgroundGradient);
      box-shadow: 0px 17px 32px -14px var(--twism-theme-shadowColor);
      cursor: pointer;
      border: none;

      p {
        margin: 0;
        opacity: 1;
        font-weight: 600;
        font-size: 14px;
        line-height: 20px;
        color: var(--twism-theme-textAboveBackground);
        transition: opacity 0.3s ease-in-out;
      }

      &--copied {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        opacity: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: opacity 0.3s ease-in-out;
        background: #23da6e;

        p {
          margin-right: 10px;
          color: white;
        }
      }

      &.-copied {
        .welcome-coin-saved--button--copied {
          opacity: 1;
        }

        > p {
          opacity: 0;
        }
      }
    }
  }
</style>
